/**
 * BoothMemorySnap — kiosk-optimised, dual-save Memory Snap flow.
 *
 * Two-stage capture:
 *   1. `live`    — camera preview + capture press
 *   2. `compose` — review polaroid, write a short note, choose pen
 *   3. `saving`  — preserving press is in flight
 *   4. `saved`   — quiet confirmation; auto-routes to QR continuity
 *
 * Dual-save semantics:
 *   • The raw JPEG is uploaded first via the offline queue
 *     (`sendOrQueue`) so the canonical preservation is guaranteed.
 *   • The rendered polaroid composite (PNG) is uploaded as a
 *     COMPANION linked by `companion_of: snap_id`. This second leg
 *     is also queued, so a network gap on it never undoes the
 *     original preservation.
 *
 * Constitutional copy locks:
 *   • Success line is always "Your memory has been preserved ✦".
 *   • Saving label is always "Preserving…".
 *   • Never "Upload", "Sync", "Success!", "Share Now".
 *
 * Behaviour preserved verbatim from the previous inline component.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Polaroid } from '../Polaroid';
import { PEN_COLORS } from '../../lib/penColors';
import { exportPolaroidPng } from '../../lib/polaroidExport';
import { sendOrQueue, newDedupeId } from '../../lib/booth-os/uploadQueue';
import { useBoothCameraPipeline } from '../../hooks/useBoothCameraPipeline';
import {
  SERIF, MAX_MESSAGE,
  Eyebrow, Heading, Lede, GoldPill,
} from './boothShellAtoms';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BoothMemorySnap = ({ theme, event, onBack, onComplete }) => {
  // 'live' | 'compose' | 'saving' | 'saved'
  const [stage, setStage] = useState('live');
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [message, setMessage] = useState('');
  const [penId, setPenId] = useState('gold');
  const [seed] = useState(() => Math.floor(Math.random() * 9_999_999) + 1);
  const [savedSnap, setSavedSnap] = useState(null);

  // iter198 — Booth Camera Pipeline. One vocabulary for every
  // capture surface. Profile 'booth' enforces:
  //   - 4:3 @ 1280×960
  //   - mirror-preview-only (export = physical room, not preview)
  //   - center crop with profile safe-zone (kills cross-device drift)
  const cam = useBoothCameraPipeline({ profile: 'booth' });
  const { videoRef, facingMode, error: cameraError, previewStyle, FramingGuide } = cam;
  // iter199 — operator-facing framing guide. Long-press on the
  // camera frame summons it for ~6s; persisted toggle lives in
  // the Retune Room. Guests never see it by default.
  const frameRef = useRef(null);

  useEffect(() => {
    if (stage === 'live') cam.start();
    else cam.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const capture = async () => {
    const data = await cam.captureStill();
    if (!data) return;
    setPhotoDataUrl(data);
    cam.stop();
    setStage('compose');
  };

  const retake = () => {
    setPhotoDataUrl(null);
    setStage('live');
  };

  const flipCamera = () => {
    cam.switchCamera();
  };

  const save = async () => {
    if (!photoDataUrl) return;
    setStage('saving');
    try {
      const res = await fetch(photoDataUrl);
      const blob = await res.blob();
      const dedupeId = newDedupeId();
      const form = new FormData();
      form.append('file', blob, `booth-snap-${Date.now()}.jpg`);
      form.append('message', message.slice(0, MAX_MESSAGE));
      form.append('pen_color', penId);
      form.append('style_seed', String(seed));
      form.append('dedupe_id', dedupeId);
      const r = await sendOrQueue({
        kind: 'multipart',
        url: `${API}/events/${event.event_id}/memory-snaps`,
        formData: form,
        dedupeId,
      });
      const snap = r.response || {
        // Local-only fallback so the polaroid review state can render
        // its companion render without waiting on the server. The
        // dedupe_id is the moment_id; the companion upload links to it.
        snap_id: dedupeId,
        message: message.slice(0, MAX_MESSAGE),
        pen_color: penId,
        pen_hex: pen.hex,
        style_seed: seed,
      };
      if (snap.photo_url && !snap.photo_url.startsWith('http')) {
        snap.photo_url = `${BACKEND_URL}${snap.photo_url}`;
      }
      setSavedSnap(snap);

      // Dual-image save: the rendered polaroid composite. Routed
      // through the queue so a network gap on this leg never undoes
      // the original snap's preservation.
      (async () => {
        try {
          const compositeBlob = await exportPolaroidPng({
            photoUrl: snap.photo_url || photoDataUrl,
            message: snap.message,
            penColor: snap.pen_hex || pen.hex,
            seed: snap.style_seed || seed,
          });
          if (!compositeBlob) return;
          const compDedupe = newDedupeId();
          const companionForm = new FormData();
          companionForm.append('file', compositeBlob, `polaroid-${snap.snap_id}.png`);
          companionForm.append('message', snap.message || '');
          companionForm.append('pen_color', snap.pen_color || penId);
          companionForm.append('style_seed', String(snap.style_seed || seed));
          companionForm.append('companion_of', snap.snap_id);
          companionForm.append('kind', 'polaroid_render');
          companionForm.append('dedupe_id', compDedupe);
          await sendOrQueue({
            kind: 'multipart',
            url: `${API}/events/${event.event_id}/memory-snaps`,
            formData: companionForm,
            dedupeId: compDedupe,
          });
        } catch { /* additive — never blocks the booth UX */ }
      })();

      setStage('saved');
      // iter210 ✦ — Personal Memory Return Path.
      // Pass back the snap_id as the contribution_id so the parent
      // (BoothOSPage) can navigate directly to the Personal Memory
      // Page instead of the legacy QRContinuity gallery screen.
      setTimeout(() => onComplete({
        deferred: !r.viaNetwork,
        contribution_id: snap.snap_id,
      }), 1800);
    } catch (e) {
      // Only reachable if IDB itself blew up.
      // eslint-disable-next-line no-console
      console.error('[BoothMemorySnap] save failed', e?.message);
      setStage('compose');
    }
  };

  const pen = PEN_COLORS.find((p) => p.id === penId) || PEN_COLORS[0];

  return (
    <div
      data-testid="booth-memory-snap"
      style={{
        minHeight: '100vh',
        display: 'flex', flexDirection: 'column',
        padding: 'clamp(24px, 4vw, 44px) clamp(20px, 4vw, 48px)',
        animation: 'editorialFade .5s ease-out',
      }}
    >
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}>
        <button
          type="button"
          onClick={onBack}
          data-testid="booth-snap-back"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: theme.inkMuted, fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 14, letterSpacing: '0.04em', padding: 8,
          }}
        >
          ← Back
        </button>
        <Eyebrow theme={theme}>✦ WRITE A MEMORY SNAP</Eyebrow>
        <span style={{ width: 60 }} aria-hidden />
      </header>

      {stage === 'live' && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 24,
        }}>
          <Heading theme={theme} size="md">Look this way ✦</Heading>
          <Lede theme={theme}>One photograph. Tap when the moment is right.</Lede>
          <div ref={frameRef} style={{
            width: 'min(620px, 90vw)', aspectRatio: '4 / 3',
            borderRadius: 14, overflow: 'hidden',
            background: '#000', border: `1px solid ${theme.hair}`,
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            position: 'relative',
          }}>
            <video
              ref={videoRef}
              playsInline muted
              data-testid="booth-snap-video"
              style={previewStyle}
            />
            <FramingGuide longPressTarget={frameRef} />
            {cameraError && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: theme.ink, fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 15, textAlign: 'center', padding: 32,
              }}>{cameraError}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <GoldPill
              theme={theme}
              onClick={capture}
              large
              testId="booth-snap-capture"
              style={{ minWidth: 220 }}
            >
              Capture this moment ✦
            </GoldPill>
            <GoldPill
              theme={theme}
              onClick={flipCamera}
              secondary
              testId="booth-snap-flip"
            >
              Flip camera
            </GoldPill>
          </div>
        </div>
      )}

      {(stage === 'compose' || stage === 'saving' || stage === 'saved') && (
        <div style={{
          flex: 1,
          display: 'grid',
          // On mobile (≤720px) the two-column polaroid+composer layout
          // overflows the viewport (2 × 280px min = 560px). Stack into
          // a single column so the polaroid and note composer sit
          // vertically with breathing room. Above 720px the editorial
          // side-by-side composition returns.
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
          gap: 'clamp(24px, 4vw, 56px)',
          alignItems: 'center', justifyItems: 'center',
          padding: 'clamp(20px, 3vw, 36px) 0',
          width: '100%',
        }}>
          <div style={{ animation: 'settle .6s ease-out' }}>
            <Polaroid
              photoUrl={photoDataUrl}
              message={message}
              penColor={pen.hex}
              seed={seed}
              width={Math.min(360, Math.max(220, Math.floor(window.innerWidth * 0.72)))}
              testId="booth-snap-polaroid"
            />
          </div>
          <div style={{ width: '100%', maxWidth: 440 }}>
            {stage === 'saved' ? (
              <div data-testid="booth-snap-saved">
                <Eyebrow theme={theme}>✦ PRESERVED</Eyebrow>
                <Heading theme={theme} size="md">Your memory has been preserved ✦</Heading>
                <Lede theme={theme} center={false}>
                  It’s settling onto the wall now. Pass the booth on
                  to the next guest — the QR is on the next screen.
                </Lede>
              </div>
            ) : (
              <>
                <Eyebrow theme={theme}>✦ A NOTE FOR THE NIGHT</Eyebrow>
                <Heading theme={theme} size="md">Add a few words ✦</Heading>
                <textarea
                  data-testid="booth-snap-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
                  placeholder="A line the night can keep…"
                  rows={3}
                  maxLength={MAX_MESSAGE}
                  style={{
                    marginTop: 18, width: '100%',
                    padding: '14px 16px',
                    borderRadius: 12,
                    background: theme.paper,
                    border: `1px solid ${theme.hair}`,
                    color: theme.ink,
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 17, lineHeight: 1.5, resize: 'none',
                    outline: 'none',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = theme.gold; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = theme.hair; }}
                />
                <div style={{
                  marginTop: 6, display: 'flex', justifyContent: 'flex-end',
                  fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 12, color: theme.inkFaint,
                }}>
                  {message.length} / {MAX_MESSAGE}
                </div>
                <PenPalette
                  theme={theme}
                  selected={penId}
                  onPick={setPenId}
                />
                <div style={{
                  marginTop: 26, display: 'flex', gap: 12,
                  flexWrap: 'wrap',
                }}>
                  <GoldPill
                    theme={theme}
                    onClick={save}
                    large
                    testId="booth-snap-save"
                    style={{ flex: 1, minWidth: 200 }}
                  >
                    {stage === 'saving' ? 'Preserving…' : 'Preserve this snap ✦'}
                  </GoldPill>
                  <GoldPill
                    theme={theme}
                    onClick={retake}
                    secondary
                    testId="booth-snap-retake"
                  >
                    Take another
                  </GoldPill>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PenPalette = ({ theme, selected, onPick }) => (
  <div style={{ marginTop: 22 }}>
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 11.5, letterSpacing: '0.18em', textTransform: 'uppercase',
      color: theme.inkFaint, marginBottom: 10,
    }}>✦ Pen colour</div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="radiogroup" aria-label="Pen colour">
      {PEN_COLORS.map((p) => (
        <button
          key={p.id}
          type="button"
          role="radio"
          aria-checked={selected === p.id}
          onClick={() => onPick(p.id)}
          data-testid={`booth-pen-${p.id}`}
          style={{
            width: 34, height: 34, borderRadius: '50%',
            background: p.hex,
            border: selected === p.id
              ? `2px solid ${theme.gold}`
              : `1px solid ${theme.hair}`,
            cursor: 'pointer',
            outline: 'none',
            boxShadow: selected === p.id
              ? `0 0 0 3px ${theme.bg}, 0 0 0 4px ${theme.gold}`
              : 'none',
            transition: 'transform 180ms ease',
          }}
          onPointerDown={(e) => { e.currentTarget.style.transform = 'scale(0.92)'; }}
          onPointerUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          onPointerLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        />
      ))}
    </div>
  </div>
);

export default BoothMemorySnap;
